import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Plus, Save, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, SHIFTS, errMsg } from "@/lib/api";

export default function SettingsPage() {
  const [shift, setShift] = useState("Morning");
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState("");

  useEffect(() => { api.get(`/tasks/${shift}`).then((r) => setTasks(r.data.tasks)); }, [shift]);

  const move = (i, d) => {
    const t = [...tasks];
    [t[i], t[i + d]] = [t[i + d], t[i]];
    setTasks(t);
  };
  const add = () => { if (newTask.trim()) { setTasks([...tasks, newTask.trim()]); setNewTask(""); } };
  const save = async () => {
    try { const r = await api.put(`/tasks/${shift}`, { tasks }); setTasks(r.data.tasks); toast.success(`${shift} tasks saved`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reset = async () => {
    if (!window.confirm(`Reset ${shift} tasks to the original DAILY TASK list?`)) return;
    const r = await api.post(`/tasks/${shift}/reset`);
    setTasks(r.data.tasks);
    toast.success("Reset to default");
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <div className="rise">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C87D55]">Settings</p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#2A3C36] mt-1">Shift Tasks</h1>
        <p className="text-sm text-[#666059] mt-2">Changes apply to new checklists. Existing checklists keep their tasks.</p>
      </div>

      <div className="flex gap-2 rise">
        {SHIFTS.map((s) => (
          <button key={s} data-testid={`settings-shift-${s.toLowerCase()}`} onClick={() => setShift(s)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${shift === s ? "bg-[#2A3C36] text-white" : "bg-[#F4F0EA] text-[#666059] hover:bg-[#EBE5DC]"}`}>{s}</button>
        ))}
      </div>

      <div className="bg-white border border-[#E2DAD0] rounded-2xl divide-y divide-[#F0EBE3] rise" data-testid="settings-task-list">
        {tasks.length === 0 && <p className="p-6 text-sm text-[#9A9188]">No tasks yet for {shift} shift. Add the first one below.</p>}
        {tasks.map((t, i) => (
          <div key={i} className="flex items-center gap-2 px-4 py-2" data-testid={`settings-task-${i}`}>
            <span className="text-xs text-[#9A9188] w-6">{i + 1}</span>
            <input value={t} data-testid={`settings-task-input-${i}`} onChange={(e) => setTasks(tasks.map((x, j) => (j === i ? e.target.value : x)))} className="flex-1 text-sm py-2 bg-transparent focus:outline-none focus:bg-[#FBF9F5] rounded px-2" />
            <button disabled={i === 0} onClick={() => move(i, -1)} data-testid={`settings-up-${i}`} className="p-1.5 text-[#9A9188] hover:text-[#2A3C36] disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
            <button disabled={i === tasks.length - 1} onClick={() => move(i, 1)} data-testid={`settings-down-${i}`} className="p-1.5 text-[#9A9188] hover:text-[#2A3C36] disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
            <button onClick={() => setTasks(tasks.filter((_, j) => j !== i))} data-testid={`settings-delete-${i}`} className="p-1.5 text-[#9A9188] hover:text-[#B91C1C]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <div className="flex gap-2 p-4">
          <input value={newTask} data-testid="settings-new-task-input" onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New task..." className="flex-1 text-sm bg-[#FBF9F5] border border-[#E2DAD0] rounded-lg px-3 py-2 focus:outline-none focus:border-[#C87D55]" />
          <Button variant="outline" className="rounded-full" onClick={add} data-testid="settings-add-button"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" className="rounded-full" onClick={reset} data-testid="settings-reset-button"><RotateCcw className="w-4 h-4 mr-1" />Reset to default</Button>
        <Button className="rounded-full bg-[#C87D55] hover:bg-[#A85832]" onClick={save} data-testid="settings-save-button"><Save className="w-4 h-4 mr-1" />Save tasks</Button>
      </div>
    </div>
  );
}
